import React, {useEffect, useCallback, useState} from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
    Button, Switch, Table, Row, Tag, Typography, Col, Modal, Icon,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../components/ConfigHeading';
import {
  selectChecklists, selectChecklistsConfig, selectInspectionReasonsPageDataRequest, selectLoadingActiveStatus,
} from '../../../../state/ui/configuration/inspection/inspectionReasonUiReducer';
import { selectInspectionReasons } from '../../../../state/domain/inspectionReasons/inspectionReasonReducer';
import {
  editInspectionReason as editInspectionReasonAction,
  requestInspectionReasonPageData, requestPublishInspectionReason,
} from '../../../../state/ui/configuration/inspection/inspectionReasonUiActions';
import EditReason from './EditReason';
import { filterPublished } from './config-util';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { ViaChecklistView } from './Checklists';
import { userIsAdmin } from '../company/user-util';

const { Column } = Table;
const { Text } = Typography;

// POP UP to show checklist details
const ChecklistTextWithModal = ({
    checkListCode, checkListDesc, checklistsConfig, activeCL,
  }) => {
    const [popUpVisible, setPopUpVisible] = useState(false);
    const checklistConfig = checklistsConfig.find(configRow => configRow.checkListCode === checkListCode);
    return (
        <div>
            <Modal
                visible={popUpVisible}
                title={
                  <span>
                    <Text>{checkListDesc}</Text>
                    {activeCL
                        ? <></>
                        : <Tag color="volcano" style={{ marginLeft: "5px" }}><FormattedMessage id="notPublished" /></Tag>
                    }
                  </span>
                }
                onCancel={() => setPopUpVisible(false)}
                footer={[
                    <Button key="submit" type="primary" onClick={() => setPopUpVisible(false)}>
                        <FormattedMessage id="close" />
                    </Button>,
                ]}
                width="70%"
                style={{ top: '50px' }}
            >
                <ViaChecklistView checklistConfig={checklistConfig}/>
            </Modal>
            <Text>{checkListDesc}</Text><Icon style={{ marginLeft: "0.5em", color: "var(--component-primary-colour)" }} type="info-circle" onClick={() => setPopUpVisible(true)}  />
        </div>
    );
};

// DESCRIPTIONS FOR CODES
const CheckListDescription = ({ code, checklists, checklistsConfig }) => {
  let item = null;
  if (checklists != null) {
    item = checklists.find(record => record.checkListCode === code);
  }
  const checklist = item == null ? null : item.description;
  const activeCL = item == null ? true : item.active;
  return (
      <Col style={{ width: "49%" }}>
        <Row type="flex" justify="start">
          {checklist
            ? <ChecklistTextWithModal checkListCode={code} checkListDesc={checklist} checklistsConfig={checklistsConfig} activeCL={activeCL} />
            : <Tag color="blue"><FormattedMessage id="noChecklist" /></Tag>
          }
        </Row>
        <Row type="flex" justify="start">
        {activeCL
          ? <></>
          : <Tag color="volcano" style={{ marginTop: "3px" }}><FormattedMessage id="notPublished" /></Tag>
        }
        </Row>
      </Col>
  );
};

// TABLE
function ReasonsTable({
  isLoading, reasons, onActiveUpdate, onEditButton, loadingActive, checklists, checklistsConfig, sessionUser,
}) {
  const isActiveLoading = useCallback(reason => !!loadingActive.find(code => `${code}` === `${reason.reasonCode}`), [loadingActive]);

  const RowTotal = () => (
    <div>
      <Text>{`${reasons.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  // EXTRA FILTERS
  function filterCompliance(value, record) {
    return record.compliance === value;
  }
  function filterChecklists(value, record) {
    if (record && value) {
       return record.checkListCodesCombined[0] === value.toString() || record.checkListCodesCombined[1] === value.toString();
    } else {
       return false;
    }
  }

  // SORTS
  function sortReasonsByDesc(a, b) {
    const aStr = `${a.reasonDescription}`;
    const bStr = `${b.reasonDescription}`;
    return aStr.localeCompare(bStr);
  }
  const sortChecklists = (a, b) => {
    const aStr = `${a.description}`;
    const bStr = `${b.description}`;
    return aStr.localeCompare(bStr);
  };

  const activeStatusChange = row => {
      const rowSend = { ...row }
      delete rowSend.checkListCodesCombined;
      onActiveUpdate(rowSend);
  }

  // TABLE SETTINGS AND COLUMNS
  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={reasons}
      rowKey={reason => reason.reasonCode}
    >
      <Column
        title={<FormattedMessage id="published" />}
        dataIndex="active"
        key="active"
        width={110}
        filtered
        filters={[{ text: <FormattedMessage id="published" />, value: true }, { text: <FormattedMessage id="notPublished" />, value: false }]}
        filteredValue={reasons.active}
        onFilter={filterPublished}
        render={(active, record) => (
          <Row type="flex" justify="center">
            {userIsAdmin(sessionUser)
              ? <Switch loading={isActiveLoading(record)} onChange={() => activeStatusChange(record)} size="small" checked={active} />
              : <Tag color={active ? 'blue' : 'volcano'}><FormattedMessage id={active ? 'published' : 'notPublished'} /></Tag> }
          </Row>
        )}
      />
      <Column
        sorter={sortReasonsByDesc}
        defaultSortOrder="ascend"
        title={<FormattedMessage id="description" />}
        width="25%"
        dataIndex="reasonDescription"
        key="reasonDescription"
      />
      <Column
        width="45%"
        title={
          <div>
              <Row type="flex" justify="start"><FormattedMessage id="checklists" /></Row>
              <Row type="flex">
                  <Col style={{ width: "49%" }}><FormattedMessage id="beforeDamage" /></Col>
                  <Col style={{ width: "2%" }}></Col>
                  <Col style={{ width: "49%" }}><FormattedMessage id="afterDamage" /></Col>
              </Row>
          </div>
        }
        dataIndex="checkListCodesCombined"
        key="checkListCodesCombined"
        filters={
          checklists
              // eslint-disable-next-line max-len
              ? [...new Set(checklists)].sort((a, b) => sortChecklists(a, b)).map(item => ({ text: item.description, value: item.checkListCode }))
              : ''
        }
        filteredValue={reasons.checkListCodesCombined}
        onFilter={filterChecklists}
        render={checkListCodesCombined => {
            if (checkListCodesCombined == null) {
                checkListCodesCombined = [null, null];
            }
            return (
          <Row type="flex" align="middle">
            <CheckListDescription
              code={checkListCodesCombined[0] == null ? '' : checkListCodesCombined[0]}
              checklists={checklists}
              checklistsConfig={checklistsConfig}
            />
            <Col style={{ width: "2%" }}></Col>
            <CheckListDescription
              code={checkListCodesCombined[1] == null ? '' : checkListCodesCombined[1]}
              checklists={checklists}
              checklistsConfig={checklistsConfig}
            />
          </Row>
        )}}
      />
      <Column
        title={<FormattedMessage id="rules" />}
        dataIndex="compliance"
        key="compliance"
        filtered
        width={100}
        filters={[{ text: <FormattedMessage id="compliance" />, value: true }, { text: <FormattedMessage id="noRules" />, value: false }]}
        filteredValue={reasons.compliance}
        onFilter={filterCompliance}
        align="left"
        render={compliance => (
          compliance
            ? <Tag className="via-tag-green">{<FormattedMessage id="compliance" />}</Tag>
            : <div />
        )}
      />
      {userIsAdmin(sessionUser)
        ? (
          <Column
            className="via-action-column"
            align="center"
            width={70}
            render={(text, record) => (
              <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">
                <FormattedMessage id="edit" />
              </Button>
            )}
          />
        )
        : ''
      }
    </Table>
  );
}

ReasonsTable.propTypes = {
  isLoading: PropTypes.bool,
  onActiveUpdate: PropTypes.func.isRequired,
  onEditButton: PropTypes.func.isRequired,
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      reasonCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      reasonDescription: PropTypes.string.isRequired,
      beforeDamageCheckListCode: PropTypes.string,
      afterDamageCheckListCode: PropTypes.string,
      compliance: PropTypes.bool.isRequired,
      active: PropTypes.bool.isRequired,
      checkListCodesCombined: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  loadingActive: PropTypes.arrayOf(PropTypes.string),
  checklists: PropTypes.arrayOf(PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
  })),
  checklistsConfig: PropTypes.arrayOf(PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    checkListCategories: PropTypes.arrayOf(PropTypes.object),
  })),
  sessionUser: PropTypes.shape({ displayName: PropTypes.string.isRequired }).isRequired,
};

ReasonsTable.defaultProps = {
  isLoading: false,
  reasons: [],
  loadingActive: [],
  checklists: [],
  checklistsConfig: [],
};

function InspectionReasons({
  isLoading, loadPageData, reasons, checklists, checklistsConfig, loadingActive,
  editInspectionReason, updateActiveStatus, sessionUser,
}) {
  // Load inspection reasons and checklists on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  // Extra validation helper
  const doesDescriptionExist = (code, value) => {
    const matchedReason = reasons.find(row => row.reasonDescription.toString().toLowerCase() === (value || '').toString().toLowerCase());
    return matchedReason ? matchedReason.reasonCode !== code : false;
  };

  // TITLE ROW
  const CreateButton = () => (
    <Button type="primary" onClick={() => editInspectionReason(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  // MAIN COMPONENT
  return (
    <ConfigHeading
      titleId="inspectionReasons"
      descriptionId="reasonsDesc"
      actions={(
          userIsAdmin(sessionUser)
            ? <CreateButton />
            : <></>
      )}
    >
      <ReasonsTable
        isLoading={isLoading}
        reasons={reasons}
        loadingActive={loadingActive}
        onActiveUpdate={updateActiveStatus}
        onEditButton={editInspectionReason}
        checklists={checklists}
        checklistsConfig={checklistsConfig}
        sessionUser={sessionUser}
      />
      <EditReason descValidator={doesDescriptionExist} />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
InspectionReasons.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  editInspectionReason: PropTypes.func.isRequired,
  updateActiveStatus: PropTypes.func.isRequired,
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      reasonCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      reasonDescription: PropTypes.string.isRequired,
      beforeDamageCheckListCode: PropTypes.string,
      afterDamageCheckListCode: PropTypes.string,
      compliance: PropTypes.bool.isRequired,
      active: PropTypes.bool.isRequired,
      checkListCodesCombined: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  checklists: PropTypes.arrayOf(PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
  })),
  checklistsConfig: PropTypes.arrayOf(PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    checkListCategories: PropTypes.arrayOf(PropTypes.object),
  })),
  loadingActive: PropTypes.arrayOf(PropTypes.string),
  sessionUser: PropTypes.shape({ displayName: PropTypes.string.isRequired }).isRequired,
};

InspectionReasons.defaultProps = {
  isLoading: false,
  reasons: [],
  checklists: [],
  checklistsConfig: [],
  loadingActive: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectInspectionReasonsPageDataRequest(state);
  const inspectionReasons = selectInspectionReasons(state);
  const checklists = selectChecklists(state);
  const checklistsConfig = selectChecklistsConfig(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    reasons: inspectionReasons,
    checklists: checklists || [],
    checklistsConfig: checklistsConfig || [],
    loadingActive: selectLoadingActiveStatus(state),
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestInspectionReasonPageData()),
  // eslint-disable-next-line max-len
  editInspectionReason: reason => dispatch(editInspectionReasonAction(reason ? reason.reasonCode : null)),
  updateActiveStatus: reason => dispatch(requestPublishInspectionReason(reason)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(InspectionReasons);
